import { useNavigate, useLocation } from "react-router-dom";
import { useState, useCallback, useMemo } from "react";
import { toast } from "react-hot-toast";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Close as CloseIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import PhoneInput from "react-phone-input-2";
import { SingleCard } from "../../layouts";
import { baseAxios } from "../../utils/config";

const FORM_ID = "create-client-dialog-form";

// Marketing defaults to UNCHECKED — a pre-checked marketing box is not valid
// affirmative consent. Appointment notifications stay pre-checked (transactional).
const INITIAL_FORM_DATA = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  birthDate: null,
  appointmentNotifications: true,
  marketing: false,
};

export default function CreateClientForm(props = {}) {
  const {
    locationId: locationIdProp,
    onSaved,
    onRequestClose,
  } = props;

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const locationIdFromURL = queryParams.get("location_id");

  const effectiveLocationId = useMemo(() => {
    if (locationIdProp != null && locationIdProp !== "") {
      return locationIdProp;
    }
    return locationIdFromURL;
  }, [locationIdProp, locationIdFromURL]);

  const isDialogMode = typeof onRequestClose === "function";

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "This field is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "This field is required";
    }

    if (!formData.phone) {
      newErrors.phone = "This field is required";
    } else if (formData.phone.length < 7 || formData.phone.length > 12) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handlePhoneChange = (phone) => {
    setFormData((prev) => ({
      ...prev,
      phone,
    }));
    if (errors.phone) {
      setErrors((prev) => ({
        ...prev,
        phone: "",
      }));
    }
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      birthDate: date,
    }));
    if (errors.birthDate) {
      setErrors((prev) => ({
        ...prev,
        birthDate: "",
      }));
    }
  };

  const handleSuccess = useCallback(
    (clientId) => {
      if (typeof onSaved === "function") {
        onSaved(clientId);
        return;
      }
      if (effectiveLocationId) {
        navigate(
          `/scheduler?client_id=${clientId}&location_id=${effectiveLocationId}`
        );
        return;
      }
      setFormData(INITIAL_FORM_DATA);
    },
    [effectiveLocationId, navigate, onSaved]
  );

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      setLoading(true);
      const {
        firstName,
        lastName,
        email,
        phone,
        birthDate,
        appointmentNotifications,
        marketing,
      } = formData;

      let date = null;
      if (birthDate && dayjs(birthDate).isValid()) {
        date = "1904/" + dayjs(birthDate).format("MM/DD");
      }

      try {
        const response = await baseAxios.post("clients", {
          client_first_name: firstName,
          client_last_name: lastName,
          client_email: email,
          client_mobile_phone_number: phone,
          client_birthday: date,
          appointment_notify_opt_in: appointmentNotifications,
          marketing_opt_in: marketing,
        });

        const clientId = response.data.client_id;
        toast.success("Client created", { duration: 3000 });
        handleSuccess(clientId);
      } catch (error) {
        toast.error(
          error?.data?.message ||
            error?.response?.data?.message ||
            "An error occurred",
          { duration: 5000 }
        );
      } finally {
        setLoading(false);
      }
    },
    [formData, validateForm, handleSuccess]
  );

  const formFields = (
    <Stack spacing={2}>
      <TextField
        label="First name"
        value={formData.firstName}
        onChange={handleInputChange("firstName")}
        required
        fullWidth
        size="small"
        disabled={loading}
        error={Boolean(errors.firstName)}
        helperText={errors.firstName}
        color="primary"
      />

      <TextField
        label="Last name"
        value={formData.lastName}
        onChange={handleInputChange("lastName")}
        required
        fullWidth
        size="small"
        disabled={loading}
        error={Boolean(errors.lastName)}
        helperText={errors.lastName}
        color="primary"
      />

      <DatePicker
        label="Birth date"
        value={formData.birthDate}
        onChange={handleDateChange}
        views={["month", "day"]}
        format="MM/DD"
        disabled={loading}
        slotProps={{
          textField: {
            size: "small",
            fullWidth: true,
            error: Boolean(errors.birthDate),
            helperText: errors.birthDate,
            color: "primary",
          },
        }}
      />

      <TextField
        label="Email"
        type="email"
        value={formData.email}
        onChange={handleInputChange("email")}
        fullWidth
        size="small"
        disabled={loading}
        error={Boolean(errors.email)}
        helperText={errors.email}
        color="primary"
      />

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.appointmentNotifications}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  appointmentNotifications: e.target.checked,
                }))
              }
              disabled={loading}
              color="primary"
            />
          }
          label="Sign up for appointment notifications"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.marketing}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  marketing: e.target.checked,
                }))
              }
              disabled={loading}
              color="primary"
            />
          }
          label="Sign up for marketing communications"
        />
      </Box>

      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 0.75, fontWeight: 500 }}
        >
          Phone number <Box component="span" color="error.main">*</Box>
        </Typography>
        <PhoneInput
          country="us"
          onlyCountries={["us", "ca"]}
          placeholder="+1 (701) 234-5678"
          value={formData.phone}
          onChange={handlePhoneChange}
          disabled={loading}
          inputStyle={{
            width: "100%",
            height: "40px",
            border: errors.phone
              ? "2px solid #d32f2f"
              : isDark
                ? "1px solid rgba(255, 255, 255, 0.23)"
                : "1px solid rgba(0, 0, 0, 0.23)",
            borderRadius: "6px",
            fontSize: "15px",
            outline: "none",
            background: isDark ? "#232326" : "#fff",
            color: isDark ? "#f2f2f2" : "#1a1a1a",
          }}
          buttonStyle={{
            height: "40px",
            borderRadius: "6px 0 0 6px",
            background: isDark ? "#232326" : "#fff",
            border: isDark
              ? "1px solid rgba(255, 255, 255, 0.23)"
              : "1px solid rgba(0, 0, 0, 0.23)",
          }}
          dropdownStyle={{
            background: isDark ? "#1b1b1e" : "#fff",
            color: isDark ? "#f2f2f2" : "#1a1a1a",
          }}
          containerStyle={{
            width: "100%",
          }}
        />
        {errors.phone && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
            {errors.phone}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  const content = (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {isDialogMode ? (
        <>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              bgcolor: "secondary.main",
              color: "common.white",
              borderBottom: "1px solid",
              borderColor: "primary.main",
              px: 3,
              py: 2,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700} color="inherit">
                Add new client
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
              >
                Create a client profile with contact and consent preferences.
              </Typography>
            </Box>
            <IconButton
              aria-label="Close"
              onClick={onRequestClose}
              disabled={loading}
              size="small"
              sx={{
                color: "primary.main",
                mt: 0.25,
                "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            <Box
              component="form"
              id={FORM_ID}
              onSubmit={onSubmit}
              sx={{ p: 3 }}
            >
              {formFields}
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              gap: 1,
            }}
          >
            <Button
              onClick={onRequestClose}
              disabled={loading}
              variant="outlined"
              color="secondary"
              sx={{ textTransform: "none", minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{
                minWidth: 140,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Create client"
              )}
            </Button>
          </DialogActions>
        </>
      ) : (
        <SingleCard title="Add New Client">
          <Box component="form" onSubmit={onSubmit} sx={{ p: 1 }}>
            {formFields}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{
                mt: 2.5,
                minHeight: 44,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Create a new client"
              )}
            </Button>
          </Box>
        </SingleCard>
      )}
    </LocalizationProvider>
  );

  return content;
}
